<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { Head, Link, useForm } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Editor from 'primevue/editor';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import InputNumber from 'primevue/inputnumber';
import InputSwitch from 'primevue/inputswitch';
import Calendar from 'primevue/calendar';
import Chips from 'primevue/chips';

defineOptions({
    layout: AdminMainLayout,
});

const props = defineProps({
    post: Object,
    categories: Array,
    tags: Array,
    statusOptions: Array,
    audienceOptions: Array,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const form = useForm({
    title: props.post?.title || '',
    excerpt: props.post?.excerpt || '',
    body: props.post?.body || '',  // Initialize as empty string
    image_url: props.post?.image_url || '',
    status: props.post?.status || 'draft',
    audience: props.post?.audience || 'both',
    pinned: props.post?.pinned ?? false,
    publish_at: props.post?.publish_at ? new Date(props.post.publish_at) : null,
    expire_at: props.post?.expire_at ? new Date(props.post.expire_at) : null,
    categories: props.post?.categories?.map(c => c.id) || [],
    tags: props.post?.tags?.map(t => t.name) || [],
    reading_time: props.post?.reading_time || null,
});

// Status dropdown options
const statusDropdownOptions = computed(() =>
    props.statusOptions.map(status => ({
        label: status.charAt(0).toUpperCase() + status.slice(1),
        value: status,
    }))
);

// Audience dropdown options
const audienceDropdownOptions = computed(() =>
    props.audienceOptions.map(audience => ({
        label: audience.charAt(0).toUpperCase() + audience.slice(1),
        value: audience,
    }))
);

// Category options for multiselect
const categoryOptions = computed(() =>
    props.categories.map(cat => ({
        label: cat.name,
        value: cat.id,
    }))
);

// Auto-calculate reading time based on body content
function calculateReadingTime() {
    if (!form.body || form.body === '<p></p>' || form.body === '<p><br></p>' || form.body.trim() === '') {
        form.reading_time = null;
        return;
    }
    // Strip HTML tags and count words
    const text = form.body.replace(/<[^>]*>/g, '');
    const wordCount = text.trim().split(/\s+/).length;
    // Assuming average reading speed of 200 words per minute
    form.reading_time = Math.ceil(wordCount / 200);
}

function submit() {
    // Calculate reading time before submission
    calculateReadingTime();

    // Debug to see what we're sending
    console.log('Form body content:', form.body);
    console.log('Form data being submitted:', {
        title: form.title,
        excerpt: form.excerpt,
        body: form.body,
        status: form.status,
        audience: form.audience,
    });

    if (props.post) {
        form.put(route('content.news.update', props.post.id), {
            onSuccess: () => {
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'News updated successfully',
                    life: 3000,
                });
            },
            onError: (errors) => {
                console.error('Submission errors:', errors);
            }
        });
    } else {
        form.post(route('content.news.store'), {
            onSuccess: () => {
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'News created successfully',
                    life: 3000,
                });
            },
            onError: (errors) => {
                console.error('Submission errors:', errors);
            }
        });
    }
}

function previewImage() {
    if (form.image_url) {
        window.open(form.image_url, '_blank');
    }
}
</script>

<template>
    <div class="p-4">
        <Head :title="post ? $t('Edit News') : $t('Create News')" />

        <div class="surface-card p-4 shadow-2 border-round">
            <div class="text-900 font-medium text-3xl mb-4" style="display:flex; align-items:center; justify-content:space-between;">
                <span>{{ post ? $t('Edit News') : $t('Create News') }}</span>
                <Link :href="route('content.news.index')" class="no-underline">
                    <Button :label="$t('Back to List')" icon="pi pi-arrow-left" severity="secondary" text />
                </Link>
            </div>

            <form @submit.prevent="submit">
                <div class="grid">
                    <!-- Basic Information -->
                    <div class="col-12 mb-2">
                        <h5 class="mb-0">{{ $t('Basic Information') }}</h5>
                    </div>

                    <div class="col-12">
                        <div class="field mb-4">
                            <label for="title" class="block text-900 font-medium mb-2">
                                {{ $t('Title') }} <span class="text-red-500">*</span>
                            </label>
                            <InputText
                                id="title"
                                v-model="form.title"
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.title }"
                            />
                            <small v-if="form.errors.title" class="p-error">
                                {{ form.errors.title }}
                            </small>
                        </div>
                    </div>

                    <div class="col-12">
                        <div class="field mb-4">
                            <label for="excerpt" class="block text-900 font-medium mb-2">
                                {{ $t('Excerpt') }}
                            </label>
                            <Textarea
                                id="excerpt"
                                v-model="form.excerpt"
                                rows="3"
                                class="w-full"
                                :placeholder="$t('Brief summary of the article (optional)')"
                                :class="{ 'p-invalid': form.errors.excerpt }"
                            />
                            <small v-if="form.errors.excerpt" class="p-error">
                                {{ form.errors.excerpt }}
                            </small>
                        </div>
                    </div>

                    <div class="col-12">
                        <div class="field mb-4">
                            <label for="body" class="block text-900 font-medium mb-2">
                                {{ $t('Content') }} <span class="text-red-500">*</span>
                            </label>
                            <!-- Temporarily using Textarea to debug the issue -->
                            <Textarea
                                id="body"
                                v-model="form.body"
                                rows="10"
                                class="w-full"
                                :placeholder="$t('Enter your article content here...')"
                                :class="{ 'p-invalid': form.errors.body }"
                                @input="calculateReadingTime"
                            />
                            <!-- Will revert to Editor once issue is fixed
                            <Editor
                                :modelValue="form.body"
                                @update:modelValue="(value) => { form.body = value; calculateReadingTime(); }"
                                editorStyle="height: 320px"
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.body }"
                            />
                            -->
                            <small v-if="form.errors.body" class="p-error">
                                {{ form.errors.body }}
                            </small>
<!--                            <small class="text-color-secondary">-->
<!--                                Note: HTML tags are supported. Rich editor temporarily disabled for debugging.-->
<!--                            </small>-->
                        </div>
                    </div>

                    <!-- Media -->
                    <div class="col-12 mt-5 mb-2">
                        <h5 class="mb-0">{{ $t('Media') }}</h5>
                    </div>

                    <div class="col-12" style="display:flex; gap:1rem;">
                        <div style="flex:1;">
                            <div class="field mb-4">
                                <label for="image_url" class="block text-900 font-medium mb-2">
                                    {{ $t('Featured Image URL') }}
                                </label>
                                <div class="p-inputgroup">
                                    <InputText
                                        id="image_url"
                                        v-model="form.image_url"
                                        placeholder="https://example.com/image.jpg"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.image_url }"
                                    />
<!--                                <Button-->
<!--                                    icon="pi pi-eye"-->
<!--                                    severity="info"-->
<!--                                    @click="previewImage"-->
<!--                                    :disabled="!form.image_url"-->
<!--                                />-->
                                </div>
                                <small v-if="form.errors.image_url" class="p-error">
                                    {{ form.errors.image_url }}
                                </small>
                            </div>
                        </div>

                        <div style="flex:1;">
<!--                    &lt;!&ndash; Categorization &ndash;&gt;-->
<!--                    <div class="col-12 mt-5 mb-2">-->
<!--                        <h5 class="mb-0">Categorization</h5>-->
<!--                    </div>-->

<!--                    <div class="col-12 md:col-6">-->
<!--                        <div class="field mb-4">-->
<!--                            <label for="categories" class="block text-900 font-medium mb-2">-->
<!--                                Categories-->
<!--                            </label>-->
<!--                            <MultiSelect-->
<!--                                id="categories"-->
<!--                                v-model="form.categories"-->
<!--                                :options="categoryOptions"-->
<!--                                optionLabel="label"-->
<!--                                optionValue="value"-->
<!--                                placeholder="Select categories"-->
<!--                                class="w-full"-->
<!--                                display="chip"-->
<!--                            />-->
<!--                        </div>-->
<!--                    </div>-->
                            <div class="field mb-4">
                                <label for="tags" class="block text-900 font-medium mb-2">
                                    {{ $t('Tags') }}
                                </label>
                                <Chips
                                    id="tags"
                                    v-model="form.tags"
                                    class="w-full"
                                    separator=","
                                    :placeholder="$t('Add tags separated by comma')"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Publishing Settings -->
                    <div class="col-12 mt-5 mb-2">
                        <h5 class="mb-0">{{ $t('Publishing Settings') }}</h5>
                    </div>

                    <div class="col-12" style="display:flex; gap:1rem;">
                        <div style="flex:1;">
                            <div class="field mb-4">
                                <label for="status" class="block text-900 font-medium mb-2">
                                    {{ $t('Status') }} <span class="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="status"
                                    v-model="form.status"
                                    :options="statusDropdownOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    class="w-full"
                                />
                            </div>
                        </div>

                        <div style="flex:1;">
                            <div class="field mb-4">
                                <label for="audience" class="block text-900 font-medium mb-2">
                                    {{ $t('Audience') }} <span class="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="audience"
                                    v-model="form.audience"
                                    :options="audienceDropdownOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    class="w-full"
                                />
                            </div>
                        </div>
                    </div>

<!--                    <div class="col-12 md:col-4">-->
<!--                        <div class="field mb-4">-->
<!--&lt;!&ndash;                            <label for="reading_time" class="block text-900 font-medium mb-2">&ndash;&gt;-->
<!--&lt;!&ndash;                                Reading Time (minutes)&ndash;&gt;-->
<!--&lt;!&ndash;                            </label>&ndash;&gt;-->
<!--&lt;!&ndash;                            <InputNumber&ndash;&gt;-->
<!--&lt;!&ndash;                                id="reading_time"&ndash;&gt;-->
<!--&lt;!&ndash;                                v-model="form.reading_time"&ndash;&gt;-->
<!--&lt;!&ndash;                                class="w-full"&ndash;&gt;-->
<!--&lt;!&ndash;                                :min="1"&ndash;&gt;-->
<!--&lt;!&ndash;                                :max="60"&ndash;&gt;-->
<!--&lt;!&ndash;                                placeholder="Auto-calculated"&ndash;&gt;-->
<!--&lt;!&ndash;                            />&ndash;&gt;-->
<!--                            <small class="text-color-secondary">-->
<!--                                {{ form.reading_time ? `${form.reading_time} min read` : 'Will be auto-calculated' }}-->
<!--                            </small>-->
<!--                        </div>-->
<!--                    </div>-->

                    <!-- Scheduling -->
                    <div class="col-12 mt-5 mb-2">
                        <h5 class="mb-0">{{ $t('Scheduling') }}</h5>
                    </div>

                    <div class="col-12" style="display:flex; gap:1rem;">
                        <div style="flex:1;">
                            <div class="field mb-4">
                                <label for="publish_at" class="block text-900 font-medium mb-2">
                                    {{ $t('Publish Date') }}
                                </label>
                                <Calendar
                                    id="publish_at"
                                    v-model="form.publish_at"
                                    showTime
                                    hourFormat="24"
                                    dateFormat="yy-mm-dd"
                                    class="w-full"
                                    placeholder="Immediately"
                                />
                            </div>
                        </div>

                        <div style="flex:1;">
                            <div class="field mb-4">
                                <label for="expire_at" class="block text-900 font-medium mb-2">
                                    {{ $t('Expire Date') }}
                                </label>
                                <Calendar
                                    id="expire_at"
                                    v-model="form.expire_at"
                                    showTime
                                    hourFormat="24"
                                    dateFormat="yy-mm-dd"
                                    class="w-full"
                                    placeholder="Never"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Settings -->
                    <div class="col-12 mt-5 mb-2">
                        <h5 class="mb-0">{{ $t('Settings') }}</h5>
                    </div>

                    <div class="col-12 md:col-6">
                        <div class="field mb-4">
                            <label class="block text-900 font-medium mb-2">{{ $t('Pinned') }}</label>
                            <InputSwitch v-model="form.pinned" />
                            <div class="mt-2">
                                <small class="text-color-secondary">{{ $t('Pin this news at the top') }}</small>
                            </div>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="col-12 mt-4">
                        <div class="flex gap-2">
                            <Button
                                type="submit"
                                :label="post ? $t('Update News') : $t('Create News')"
                                icon="pi pi-save"
                                severity="primary"
                                :loading="form.processing"
                            />
                            <Link :href="route('content.news.index')" class="no-underline">
                                <Button
                                    :label="$t('Cancel')"
                                    severity="secondary"
                                    text
                                />
                            </Link>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>
